<template>
  <v-card flat class="px-2 px-md-4">
    <!-- 醫師登入：顯示 Coming Soon -->
    <v-sheet
      v-if="isDoctorLogin"
      height="calc(100vh - 350px)"
      class="d-flex align-center justify-center"
    >
      <div class="grey-3--text text-md-h1 text-h3 font-weight-bold text-center">
        COMING SOON
      </div>
    </v-sheet>

    <!-- 診所登入：顯示帳號管理 -->
    <template v-else>
      <!-- 標題列 -->
      <GuideTour config-key="settings.accountPermission" clearAllSubtitle />

      <v-row class="mb-4" align="center" justify="end">
        <v-col cols="auto">
          <v-btn color="teal" dark @click="openCreateDialog">
            <v-icon left>mdi-plus</v-icon>
            新增帳號
          </v-btn>
        </v-col>
      </v-row>

      <!-- 帳號列表 -->
      <AccountList
        :accounts="accounts"
        @edit-permissions="openEditPermissionsDialog"
        @edit-credentials="openEditCredentialsDialog"
        @copy-account="openCopyAccountDialog"
        @delete-account="openDeleteDialog"
      />

      <!-- 編輯權限 Dialog -->
      <AccountPermissionsDialog
        v-model="editPermissionsDialog"
        :account="selectedAccount"
        :availablePermissions="availablePermissions"
        @submit="handleUpdatePermissions"
      />

      <!-- 設定帳密 Dialog -->
      <AccountCredentialsDialog
        v-model="editCredentialsDialog"
        :account="selectedAccount"
        @submit="handleUpdateCredentials"
      />

      <!-- 新增帳號 Dialog -->
      <AccountCreateDialog
        v-model="createDialog"
        :availablePermissions="availablePermissions"
        :initialPermissions="initialPermissions"
        :isCopyMode="isCopyMode"
        @submit="handleCreateAccount"
      />

      <!-- 刪除帳號確認 Dialog -->
      <AccountDeleteDialog
        v-model="deleteDialog"
        :account="accountToDelete"
        @confirm="handleDeleteAccount"
      />

      <!-- 錯誤訊息 Dialog -->
      <ConfirmDialog
        v-model="errorDialog"
        :title="errorTitle"
        :content="errorMessage"
        confirmBtnText="確定"
        cancelBtnText=""
      />
    </template>
  </v-card>
</template>

<script>
import { mapGetters, mapMutations, mapState } from 'vuex'
import {
  getPermissionsAPI,
  getUsersAPI,
  createUserAPI,
  updateUserCredentialsAPI,
  updateUserPermissionsAPI,
  deleteUserAPI
} from '@/api/permission'
import { CRM_PERMISSIONS } from '@/constants/permissions'
import GuideTour from '@/components/utils/GuideTour.vue'
import ConfirmDialog from '@/components/utils/ConfirmDialog.vue'
import AccountList from '@/components/account/AccountList.vue'
import AccountPermissionsDialog from '@/components/account/AccountPermissionsDialog.vue'
import AccountCredentialsDialog from '@/components/account/AccountCredentialsDialog.vue'
import AccountCreateDialog from '@/components/account/AccountCreateDialog.vue'
import AccountDeleteDialog from '@/components/account/AccountDeleteDialog.vue'

export default {
  name: 'SubAccountSettings',
  components: {
    GuideTour,
    ConfirmDialog,
    AccountList,
    AccountPermissionsDialog,
    AccountCredentialsDialog,
    AccountCreateDialog,
    AccountDeleteDialog
  },
  computed: {
    ...mapGetters([
      'axiosParamData'
    ]),
    ...mapState({
      user: state => state.user
    }),
    // 判斷是否為醫師登入（type: "2"）
    isDoctorLogin() {
      return this.user.type === '2'
    }
  },
  data() {
    return {
      // 帳號資料（從 API 載入）
      accounts: [],

      // 可用權限列表
      availablePermissions: [],

      // Dialog 狀態
      editPermissionsDialog: false,
      editCredentialsDialog: false,
      createDialog: false,
      deleteDialog: false,
      errorDialog: false,

      // 選中的帳號（用於編輯）
      selectedAccount: null,

      // 刪除相關
      accountToDelete: null,

      // 新增帳號相關
      initialPermissions: null,
      isCopyMode: false,

      // 錯誤訊息相關
      errorTitle: '',
      errorMessage: ''
    }
  },

  mounted() {
    // 載入權限列表
    this.loadPermissions()
    // 載入帳號列表
    this.loadAccounts()
  },

  methods: {
    ...mapMutations({
      clearUserData: 'clearUserData'
    }),
    // ===== 顯示錯誤訊息 =====
    showError(title, error) {
      // 優先從 response.data.messages 取得錯誤訊息（陣列）
      let message = error.response?.data?.messages

      // 如果 messages 是陣列，取第一個
      if (Array.isArray(message) && message.length > 0) {
        message = message[0]
      }

      // 如果沒有 messages，嘗試從 message 取得
      if (!message) {
        message = error.response?.data?.message || error.message || '發生未知錯誤'
      }

      this.errorTitle = title
      this.errorMessage = message
      this.errorDialog = true
    },

    // ===== 載入權限列表 =====
    async loadPermissions() {
      try {
        const response = await getPermissionsAPI(this.axiosParamData)
        this.availablePermissions = response.data.permissions
      } catch (error) {
        console.error('載入權限列表失敗:', error)
        // Fallback: 使用本地常數
        this.availablePermissions = CRM_PERMISSIONS.map((p, index) => ({
          id: index + 1,
          name: p.name,
          code_name: p.code_name
        }))
      }
    },

    // ===== 載入帳號列表 =====
    async loadAccounts() {
      try {
        const response = await getUsersAPI(this.axiosParamData)
        this.accounts = response.data.users
      } catch (error) {
        console.error('載入帳號列表失敗:', error)
        this.showError('載入帳號列表失敗', error)
      }
    },

    // ===== 編輯權限 =====
    openEditPermissionsDialog(account) {
      this.selectedAccount = account
      this.editPermissionsDialog = true
    },

    async handleUpdatePermissions({ id, permissions }) {
      try {
        // 呼叫 API（注意：後端參數名稱是 route_permissions）
        await updateUserPermissionsAPI(id, { ...this.axiosParamData, route_permissions: permissions })

        // 重新載入帳號列表（確保資料同步）
        await this.loadAccounts()

        // 關閉 Dialog
        this.editPermissionsDialog = false

        // 顯示成功訊息
        this.$notify.success('更新權限成功！')
      } catch (error) {
        console.error('更新權限失敗:', error)
        this.showError('更新權限失敗', error)
      }
    },

    // ===== 設定帳密 =====
    openEditCredentialsDialog(account) {
      this.selectedAccount = account
      this.editCredentialsDialog = true
    },

    async handleUpdateCredentials({ id, name, account, password, email }) {
      try {
        // 判斷是否修改的是當前登入用戶自己，且修改了帳號或密碼
        const isEditingSelf = Number(id) === Number(this.user.crm_user_id)
        const originalAccount = this.selectedAccount?.account
        const originalPassword = this.selectedAccount?.password

        const hasCredentialChange = account !== originalAccount || password !== originalPassword

        // 呼叫 API
        await updateUserCredentialsAPI(id, { ...this.axiosParamData, name, account, password, email })

        // 關閉 Dialog
        this.editCredentialsDialog = false

        if (isEditingSelf && hasCredentialChange) {
          // 修改自己的帳號或密碼，強制登出重新登入
          // 維持原 alert blocking 語義：等使用者按確認再清資料登出
          this.$store.dispatch('notify/showAlert', {
            message: '帳號或密碼已修改，請重新登入。',
            onConfirm: () => this.clearUserData(),
          })
        } else {
          // 只修改 name/email 或修改其他帳號，正常流程
          await this.loadAccounts()
          this.$notify.success('更新帳密成功！')
        }
      } catch (error) {
        console.error('更新帳密失敗:', error)
        this.showError('更新帳密失敗', error)
      }
    },

    // ===== 新增帳號 =====
    openCreateDialog() {
      this.initialPermissions = null
      this.isCopyMode = false
      this.createDialog = true
    },

    openCopyAccountDialog(account) {
      this.initialPermissions = [...account.route_permissions]
      this.isCopyMode = true
      this.createDialog = true
    },

    async handleCreateAccount(formData) {
      try {
        // 呼叫 API（注意：後端參數名稱是 route_permissions）
        await createUserAPI({
          ...this.axiosParamData,
          name: formData.name,
          account: formData.account,
          password: formData.password,
          email: formData.email,
          route_permissions: formData.permissions
        })

        // 重新載入帳號列表（確保資料同步）
        await this.loadAccounts()

        // 關閉 Dialog
        this.createDialog = false

        // 顯示成功訊息
        this.$notify.success('新增帳號成功！')
      } catch (error) {
        console.error('新增帳號失敗:', error)
        this.showError('新增帳號失敗', error)
      }
    },

    // ===== 刪除帳號 =====
    openDeleteDialog(account) {
      this.accountToDelete = account
      this.deleteDialog = true
    },

    async handleDeleteAccount(account) {
      try {
        // 呼叫 API
        await deleteUserAPI(account.id, this.axiosParamData)

        // 重新載入帳號列表（確保資料同步）
        await this.loadAccounts()

        // 關閉 Dialog
        this.deleteDialog = false
        this.accountToDelete = null

        // 顯示成功訊息
        this.$notify.success('刪除帳號成功！')
      } catch (error) {
        console.error('刪除帳號失敗:', error)
        this.showError('刪除帳號失敗', error)
      }
    }
  }
}
</script>
